<template>
	<DialogV2 ref="dialog"
		:title="type === 'success' ? $t('text.updateSuccessful') : $t('text.updateFailed')"
		:confirmLabel="$t('text.continue')"
		:onConfirm="resolve"
		:hideClose="false"
		hide-cancel
		width="520px"
		height="auto"
		@close="resolve"
	>
		<template #content>
			<div class="ResultDialogs" :data-cy="`resultDialog-${type}`">
				<div class="result-header">
					<v-icon v-if="type === 'success'" size="48" color="#64c823">mdi-check-circle</v-icon>
					<v-icon v-else size="48" color="#f34545">mdi-close-circle</v-icon>
					<div>
						<div class="result-title">
							<template v-if="type === 'success'">{{ $t('text.updateSuccessful') }}</template>
							<template v-else-if="type === 'cancelFailed'">{{ $t('text.somethingWentWrong') }}</template>
							<template v-else>{{ $t('text.updateFailed') }}</template>
						</div>
						<div class="result-text">
							<template v-if="type === 'success'">{{ $t('text.orderUpdatedSuccessfully') }}</template>
							<template v-else-if="type === 'cancelFailed'">{{ $t('text.contingentReleaseFailed') }}</template>
							<template v-else-if="requestError">{{ reasonText(requestError) }}</template>
							<template v-else>{{ $t('text.rebookingItemsFailed') }}</template>
						</div>
					</div>
				</div>
				<div v-if="type === 'failed' && affectedItems.length" class="affected">
					<div class="affected-title">{{ $t('text.affectedItems') }}</div>
					<ul>
						<li v-for="entry in affectedItems" :key="entry.item.orderItemId">
							<div class="affected-name">{{ entry.item.name }}</div>
							<div class="affected-detail">{{ formatDateTime(entry.item.consumptionDate, entry.item.consumptionTime) }} ({{ personName(entry.item) }})</div>
							<div class="affected-detail">
								{{ [entry.item.ticketCategory, entry.item.ticketType].filter(Boolean).join(', ') }}<template v-if="entry.item.serviceProviderName"> | {{ entry.item.serviceProviderName }}</template>
							</div>
							<div class="affected-reason">{{ reasonText(entry.error) }}</div>
						</li>
					</ul>
				</div>
			</div>
		</template>
	</DialogV2>
</template>

<script>
import DialogV2 from '../../../components/common/DialogV2.vue'
import { formatDateTime, reasonKeyForCode } from './rebooking-utils'

export default {
	name: 'ResultDialogs',
	components: { DialogV2 },
	data: () => ({
		type: 'success', // 'success' | 'failed' | 'cancelFailed'
		affectedItems: [], // [{ item, error: { code, message } | null }]
		requestError: null, // top-level PEAK error { code, message }
		resolver: null,
	}),
	methods: {
		formatDateTime,
		personName(item) {
			return [item.personalization?.firstName, item.personalization?.lastName].filter(Boolean).join(' ')
		},
		// localized reason for a PEAK error/warning code, with graceful fallbacks:
		// mapped code -> translation; else PEAK's raw message; else a generic reason.
		reasonText(error) {
			if (!error) return this.$t('text.rebookGenericItemError')
			const key = reasonKeyForCode(error.code)
			if (key) return this.$t('text.' + key)
			return error.message || this.$t('text.rebookGenericItemError')
		},
		// resolves when the user clicks Continue (or closes the dialog)
		open(type, { affectedItems = [], requestError = null } = {}) {
			this.type = type
			this.affectedItems = affectedItems
			this.requestError = requestError
			this.$refs.dialog.open()
			return new Promise(resolve => { this.resolver = resolve })
		},
		resolve() {
			this.resolver?.()
			this.resolver = null
		},
	},
}
</script>

<style scoped>
.result-header {
	display: flex;
	gap: 16px;
	align-items: flex-start;
	margin-bottom: 8px;
}
.result-title { font-size: 17px; font-weight: 600; }
.result-text { color: #666; }
.affected { margin-top: 16px; padding-left: 64px; }
.affected-title { font-weight: 600; margin-bottom: 8px; }
.affected ul { padding-left: 20px; }
.affected li { margin-bottom: 12px; }
.affected-name { font-weight: 500; }
.affected-detail { font-size: 12px; color: #8f8f8f; }
.affected-reason { font-size: 12.5px; color: #c43030; margin-top: 2px; }
</style>
